import React, { useEffect, useState } from "react";
import ReactStars from "react-rating-stars-component";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addToWishlist } from "../features/products/productSlilce";
import { getuserProductWishlist, addToWishlistGuest, addProdToCart, getUserCart, updateCartProduct } from "../features/user/userSlice";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { BsEye, BsCartPlus } from "react-icons/bs";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { dummyReviews, calculateAverageRating } from "../utils/dummyReviews";

const ProductCard = (props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { grid, data } = props;
  const location = useLocation();

  const wishlistState = useSelector((state) => state?.auth?.wishlist?.wishlist);
  const userCartState = useSelector((state) => state?.auth?.cartProducts);
  const guestCartItems = useSelector((state) => state?.auth?.guestCart);
  const [wishlist, setWishlist] = useState([]);
  const getTokenFromLocalStorage = localStorage.getItem("customer")
    ? JSON.parse(localStorage.getItem("customer"))
    : null;

  const config2 = {
    headers: {
      Authorization: `Bearer ${
        getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
      }`,
      Accept: "application/json",
    },
  };
    
  useEffect(() => {
    // Fetch wishlist when component mounts
    if(getTokenFromLocalStorage!=null){
         dispatch(getuserProductWishlist());
    }
  }, [dispatch]);

  useEffect(() => {
    if (wishlistState) {
      setWishlist(wishlistState);
    }
  }, [wishlistState]);

  const isProductInWishlist = (productId) => {
    // Check if user is logged in
    if (getTokenFromLocalStorage) {
      return wishlist?.some((item) => item._id === productId);
    } else {
      // For guest users, check localStorage
      const guestWishlist = JSON.parse(localStorage.getItem("guestWishlist")) || [];
      return guestWishlist.includes(productId);
    }
  };

  const handleWishlistToggle = async (productId, e) => {
    e.stopPropagation();
    try {
      // Check if user is logged in
      if (getTokenFromLocalStorage) {
        const result = await dispatch(addToWishlist(productId)).unwrap();
        // Refresh wishlist after adding/removing ONLY if successful
        if (result) {
          dispatch(getuserProductWishlist());
        }
      } else {
        // For guest users, use guest wishlist functionality
        await dispatch(addToWishlistGuest(productId)).unwrap();
      }
    } catch (error) {
      // Handle rate limiting error specifically
      if (error?.response?.status === 429) {
        toast.error("Too many requests. Please wait before trying again.");
      } else {
        toast.error("Failed to update wishlist. Please try again.");
      }
    }
  };

  const handleAddToCart = async (product, e) => {
    e.stopPropagation();
    try {
      // Get current cart items from Redux state
      const currentCart = getTokenFromLocalStorage 
        ? (userCartState || []) 
        : (guestCartItems || []);
      
      // Check if product already exists in cart
      let existingItem = null;
      
      if (getTokenFromLocalStorage) {
        // For authenticated users, find by productId
        existingItem = currentCart.find(item => item.productId?._id === product?._id);
      } else {
        // For guest users, find by productId
        existingItem = currentCart.find(item => item.productId === product?._id);
      }

      if (existingItem) {
        // If product already exists, update quantity using updateCartProduct
        const cartData = {
          cartItemId: existingItem._id || existingItem.tempId,
          quantity: existingItem.quantity + 1
        };
        
        await dispatch(updateCartProduct(cartData)).unwrap();
      } else {
        // If product doesn't exist, add new item with quantity 1
        const cartData = {
          productId: product?._id,
          quantity: 1,
          price: product?.price,
          isPreorder: product?.isPreorder || false,
          preorderDeposit: product?.preorderDeposit || 0
        };
        
        await dispatch(addProdToCart(cartData)).unwrap();
        toast.success("Product added to cart!");
      }
      
      // Refresh cart state
      dispatch(getUserCart(config2));
      
      // Navigate to cart page
      setTimeout(() => {
        navigate("/cart");
      }, 500);
    } catch (error) {
      toast.error("Failed to add product to cart");
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const getGridClass = () => {
    // If grid prop is provided, use it to determine responsive classes
    if (grid) {
      switch (grid) {
        case 12:
          return "col-12";
        case 6:
          return "col-6 col-lg-6";
        case 4:
          return "col-6 col-md-4 col-lg-4";
        case 3:
          return "col-6 col-md-4 col-lg-3";
        default:
          return ""; // Return empty string to use parent's grid classes
      }
    }
    
    // If no grid prop or on product page, use default responsive classes
    if (location.pathname === "/product") {
      return "col-6 col-md-4 col-lg-3";
    }
    
    // For other pages, return empty string to use parent's grid classes
    return "";
  };

  // Get current language from i18n
  const currentLanguage = i18n.language;

  // Function to calculate combined rating count for Ganesha and Hanuman products
  const getCombinedRatingInfo = (product) => {
    const productName = product?.title || '';
    let combinedRatings = [...(product?.ratings || [])];
    let combinedTotalRating = product?.totalrating || 0;
    let combinedReviewCount = combinedRatings.length;

    if (productName.toLowerCase().includes("ganesha")) {
      // For Ganesha products, show 18 + original review length as per requirement
      combinedRatings = [...dummyReviews.ganesha, ...combinedRatings];
      combinedTotalRating = calculateAverageRating(combinedRatings);
      combinedReviewCount = 18 + (product?.ratings?.length || 0);
    } else if (productName.toLowerCase().includes("hanuman")) {
      // Add Hanuman dummy reviews to the combined ratings
      combinedRatings = [...dummyReviews.hanuman, ...combinedRatings];
      combinedTotalRating = calculateAverageRating(combinedRatings);
      combinedReviewCount = combinedRatings.length;
    }

    return {
      ratingValue: combinedTotalRating,
      reviewCount: combinedReviewCount
    };
  };

  return (
    <>
      {data?.map((item, index) => {
        const isWishlist = isProductInWishlist(item._id);
        // Get translated content for the product
        const translatedTitle = item?.translations?.[currentLanguage]?.title || item?.title || "";
        const translatedBrand = item?.translations?.[currentLanguage]?.brand || item?.brand || "";
        // Calculate combined rating info for Ganesha and Hanuman products
        const ratingInfo = getCombinedRatingInfo(item);
        return (
          <div key={index} className={`${getGridClass()} mb-4`}>
            <div 
              className="product-card position-relative h-100 cursor-pointer"
              style={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                border: '1px solid #f0f0f0',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
              onClick={() => handleProductClick(item._id)}
            >
              {/* Wishlist Icon */}
              <div className="wishlist-icon position-absolute top-0 end-0 m-3" style={{ zIndex: 10 }}>
                <button
                  className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center"
                  style={{ 
                    width: '32px', 
                    height: '32px',
                    border: 'none',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                  }}
                  onClick={(e) => handleWishlistToggle(item._id, e)}
                  title={isWishlist ? "Remove from Wishlist" : "Add to Wishlist"}
                >
                  {isWishlist ? (
                    <AiFillHeart className="text-danger" size={16} style={{ color: '#e91e63' }} />
                  ) : (
                    <AiOutlineHeart className="text-muted" size={16} />
                  )}
                </button>
              </div>

              {/* Badges Container */}
              <div className="badges-container position-absolute top-0 start-0 m-3 d-flex flex-column gap-2" style={{ zIndex: 10 }}>
                {/* Discount Badge */}
                {item?.tags?.includes("sale") && (
                  <span className="badge px-2 py-1" style={{
                    backgroundColor: '#fff',
                    color: '#e91e63',
                    border: '1.5px solid #e91e63',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: '700',
                    letterSpacing: '0.3px'
                  }}>
                    SALE
                  </span>
                )}

                {/* New Badge */}
                {item?.tags?.includes("new") && (
                  <span className="badge px-2 py-1" style={{
                    backgroundColor: '#fff',
                    color: '#9c27b0',
                    border: '1.5px solid #9c27b0',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: '700',
                    letterSpacing: '0.3px'
                  }}>
                    NEW
                  </span>
                )}

                {/* Preorder Badge */}
                {item?.isPreorder && (
                  <span className="badge px-2 py-1" style={{
                    backgroundColor: '#e91e63',
                    color: '#fff',
                    border: '1.5px solid #e91e63',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: '700',
                    letterSpacing: '0.3px'
                  }}>
                    🕒
                    PREORDER
                  </span>
                )}

                {/* Coming Soon Badge */}
                {item?.tags?.includes("comingsoon") && (
                  <span className="badge px-2 py-1" style={{
                    backgroundColor: 'rgb(223, 97, 97)',
                    color: '#fff',
                    border: '1.5px solid rgb(223, 97, 97)',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: '700',
                    letterSpacing: '0.3px'
                  }}>
                    COMING SOON
                  </span>
                )}
              </div>

              {/* Product Image */}
              <div className="rounded-xl position-relative" style={{ backgroundColor: '#f8f9fa' }}>
                <img
                  src={item?.images?.[0]?.url || "https://via.placeholder.com/300x300?text=Product"}
                  className="img-fluid w-100 rounded-xl"
                  alt={translatedTitle || "Product"}
                  style={{ 
                    height: '220px', 
                    objectFit: 'cover',
                    cursor: 'pointer'
                  }}
                />
                
                {/* Action Bar - Only visible on hover */}
                <div 
                  className="action-bar position-absolute bottom-0 end-0 me-3 mb-3"
                  style={{
                    opacity: 0,
                    transition: 'opacity 0.3s ease'
                  }}
                >
                  <div className="d-flex flex-column gap-2">
                    <button 
                      className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center"
                      style={{ 
                        width: '32px', 
                        height: '32px',
                        border: 'none',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProductClick(item._id);
                      }}
                      title="View Details"
                    >
                      <BsEye style={{ color: '#e91e63' }} size={14} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Product Details */}
              <div className="product-details d-flex flex-column" style={{ padding: '16px', minHeight: '240px' }}>
                <div className="brand mb-1">
                  <span style={{
                    fontSize: '12px',
                    color: '#999',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px'
                  }}>
                    {translatedBrand || "Brand"}
                  </span>
                </div>
                
                <h6 className="product-title mb-2 fredoka" style={{
                  fontWeight: '600',
                  fontSize: '14px',
                  lineHeight: '1.4',
                  color: '#333',
                  marginBottom: '8px'
                }}>
                  {grid === 12 || grid === 6
                    ? translatedTitle
                    : translatedTitle?.length > 50
                    ? translatedTitle?.substring(0, 50) + "..."
                    : translatedTitle}
                </h6>
                
                {!item?.tags?.includes("comingsoon") && (
                  <div className="rating mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <ReactStars
                        count={5}
                        size={14}
                        value={ratingInfo.ratingValue || 0}
                        edit={false}
                        activeColor="#ffc107"
                      />
                      <span style={{ 
                        color: '#999', 
                        fontSize: '12px' 
                      }}>
                        ({ratingInfo.reviewCount || 0})
                      </span>
                    </div>
                  </div>
                )}

                <div className="bg-white mb-2 flex-grow-0">
                  {/* Display discounted price and original price with discount percentage */}
                  {item?.discountedPrice && item?.originalPrice && item?.discountedPrice < item?.originalPrice ? (
                    <div className="d-flex align-items-center gap-2 bg-white">
                      <span style={{
                        fontWeight: '700',
                        fontSize: '18px',
                        color: '#e91e63'
                      }}>
                        ₹{item?.discountedPrice?.toLocaleString()}
                      </span>
                      <span style={{
                        textDecoration: 'line-through',
                        fontSize: '14px',
                        color: '#999',
                        fontWeight: '500'
                      }}>
                        ₹{item?.originalPrice?.toLocaleString()}
                      </span>
                      <span style={{
                        backgroundColor: '#e91e63',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}>
                        {Math.round(((item?.originalPrice - item?.discountedPrice) / item?.originalPrice) * 100)}% OFF
                      </span>
                    </div>
                  ) : item?.price ? (
                    <span className="bg-white" style={{
                      fontWeight: '700',
                      fontSize: '18px',
                      color: '#333'
                    }}>
                      ₹{item?.price?.toLocaleString()}
                    </span>
                  ) : (
                    <span className="bg-white" style={{
                      fontWeight: '700',
                      fontSize: '18px',
                      color: '#333'
                    }}>
                      ₹{item?.discountedPrice?.toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Preorder Info - Fixed Height */}
                <div className="preorder-info mb-2" style={{ minHeight: '48px' }}>
                  {item?.isPreorder && (
                    <>
                      <div className="d-flex align-items-center gap-1 mb-1">
                        <span className="badge px-2 py-1" style={{
                          backgroundColor: '#fce4ec',
                          color: '#e91e63',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          Preorder Available
                        </span>
                      </div>
                      {item?.preorderDeposit > 0 && (
                        <div className="mt-1">
                          <small style={{
                            color: '#666',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            Deposit: ₹{item?.preorderDeposit?.toLocaleString()}
                          </small>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Add to Cart Button */}
                <button 
                  className="btn w-100 d-flex align-items-center justify-content-center gap-2 mt-auto"
                  style={{
                    backgroundColor: '#e91e63',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '12px 16px',
                    fontSize: '13px',
                    fontWeight: '700',
                    transition: 'all 0.3s ease',
                    letterSpacing: '0.3px'
                  }}
                  onClick={(e) => handleAddToCart(item, e)}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#c2185b';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#e91e63';
                    e.target.style.transform = 'translateY(0)';
                  }}
                  disabled={item?.quantity <= 0 && !item?.isPreorder}
                >
                  <BsCartPlus size={16} />
                  {item?.isPreorder ? "Preorder Now" : "Add to Cart"}
                </button>

                {/* Stock Status */}
                {item?.quantity <= 0 && !item?.isPreorder && (
                  <div className="stock-status mt-2 text-center">
                    <span className="badge" style={{
                      backgroundColor: '#fff3f3',
                      color: '#e91e63',
                      border: '1px solid #e91e63',
                      fontSize: '11px',
                      fontWeight: '600',
                      padding: '4px 12px'
                    }}>
                      Out of Stock
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* CSS for hover effects */}
            <style jsx>{`
              .product-card:hover {
                transform: translateY(-4px);
                box-shadow: 0 8px 24px rgba(233, 30, 99, 0.15) !important;
              }
              .product-card:hover .action-bar {
                opacity: 1 !important;
              }
            `}</style>
          </div>
        );
      })}
    </>
  );
};

export default ProductCard;